import { PostHog as RealPostHog } from "@app/analytics/posthog";
import { Platform } from "expo-modules-core";
import { sentry } from "@app/util/sentry";
import { KeyedDebouncer } from "@app/analytics/KeyedDebouncer";
import {
  AnalyticsEvents,
  EventNames,
  NoEventProperties,
} from "@app/analytics/Events";
import { ENV } from "@app/config/env";

const analyticsDisabled = ENV.disableAnalytics || !ENV.posthogApiKey;
if (analyticsDisabled) {
  console.info("Analytics is disabled.");
}

const NoOpPostHog = {
  initialize: () => undefined,
  trackEvent: () => undefined,
  setUser: () => undefined,
  setUserProperties: () => undefined,
  getSessionId: async () => Promise.resolve(""),
  getAnonymousId: async () => Promise.resolve(""),
} as typeof RealPostHog;

let PostHog = analyticsDisabled ? NoOpPostHog : RealPostHog;

// Analytics must never break the app, so every call into the SDK is guarded.
function reportAnalyticsError(error: unknown, operation: string) {
  console.error(`Analytics ${operation} failed`, error);
  sentry.captureException(error, { extra: { analyticsOperation: operation } });
}

function guarded(operation: string, fn: () => void) {
  try {
    fn();
  } catch (e) {
    reportAnalyticsError(e, operation);
  }
}

async function guardedAsync<T>(
  operation: string,
  fn: () => Promise<T>,
  fallback: T
): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    reportAnalyticsError(e, operation);
    return fallback;
  }
}

const trackEvent = (
  eventName: string,
  eventProperties?: Record<string, unknown>
) => {
  sentry.addBreadcrumb({
    type: "debug",
    level: "info",
    category: "Analytics.Event",
    message: eventName,
    data: eventProperties,
  });
  guarded("trackEvent", () => PostHog.trackEvent(eventName, eventProperties));
};

const keyedDebouncer = new KeyedDebouncer(trackEvent, 5000, {
  // we want to track events immediately and ignore duplicate events for up to x seconds.
  // This helps ensure events are ordered correctly and do not affect funnels
  // that require specific order of events
  leading: true,
  trailing: false,
});

type AnalyticsInterface = {
  initialize: () => void;
  trackEvent: <T extends EventNames>(
    eventName: T,
    ...eventProperties: AnalyticsEvents[T] extends NoEventProperties
      ? []
      : [AnalyticsEvents[T]]
  ) => void;
  trackEventDebounced: <T extends EventNames>(
    debounceKey: string,
    eventName: T,
    ...eventProperties: AnalyticsEvents[T] extends NoEventProperties
      ? []
      : [AnalyticsEvents[T]]
  ) => void;
  setUser: (userId: string | null) => void;
  setUserProperties: (properties: Record<string, unknown>) => void;

  /**
   * Call is async because on mobile the session is maintained in the iOS/Android libraries.
   * Communication between JS and Native was done Asynchronously
   */
  getSessionId: () => Promise<string>;
  getAnonymousId: () => Promise<string | undefined | null>;
};

export const Analytics: AnalyticsInterface = {
  initialize() {
    try {
      PostHog.initialize(ENV.posthogApiKey!, ENV.posthogHost);
    } catch (e) {
      reportAnalyticsError(e, "initialize");
      PostHog = NoOpPostHog;
      return;
    }
    if (Platform.OS === "web") {
      window.addEventListener("beforeunload", () => {
        guarded("trackEvent", () => PostHog.trackEvent("Leave App"));
      });
    }
  },
  trackEvent,
  trackEventDebounced: (debounceKey, eventName, eventProperties?) => {
    keyedDebouncer.get(debounceKey + eventName)(eventName, eventProperties);
  },
  setUser: (userId) => {
    guarded("setUser", () => PostHog.setUser(userId));
  },
  setUserProperties: (properties: Record<string, unknown>) => {
    guarded("setUserProperties", () => PostHog.setUserProperties(properties));
  },
  getSessionId: () => {
    return guardedAsync("getSessionId", () => PostHog.getSessionId(), "");
  },
  getAnonymousId: () => {
    return guardedAsync("getAnonymousId", () => PostHog.getAnonymousId(), null);
  },
};
